import { createSelector, createSlice } from '@reduxjs/toolkit';

export const blocksSlice = createSlice({
  name: 'blocks',
  initialState: {
    activeType: 'test',
    activeUID: 0,
    dragList: [],
    list: [],
    initialList: [],
    archivedList: [],
    isLoadingArchived: false,
  },
  reducers: {
    blocksUpdateActiveType: (state, action) => {
      state.activeType = action.payload;
    },
    blocksUpdateActiveUID: (state, action) => {
      state.activeUID = action.payload;
    },
    blocksUpdateDragList: (state, action) => {
      state.dragList = action.payload;
    },
    blocksUpdateList: (state, action) => {
      state.list = action.payload;
    },
    blocksUpdateInitialList: (state, action) => {
      state.initialList = action.payload;
    },
    blocksUpdateArchivedList: (state, action) => {
      state.archivedList = action.payload;
    },
    blocksUpdateIsLoadingArchived: (state, action) => {
      state.isLoadingArchived = action.payload;
    },
    // Replace a single block in the list by matching id/sid
    blocksReplaceBlock: (state, action) => {
      const newBlock = action.payload;
      const ubId = newBlock.id ?? newBlock.Id;
      const ubSid = newBlock.sid ?? newBlock.SID;
      const idx = state.list.findIndex((b) => {
        const bId = b.id ?? b.Id;
        const bSid = b.sid ?? b.SID;
        return bId === ubId || bSid === ubSid;
      });
      if (idx !== -1) {
        state.list[idx] = newBlock;
      }
    },
    // Triggers `GET /api/profiles/{id}/blocks/meta` saga. Payload is ignored.
    blocksRefreshMetaRequest: () => {},
    // Merges fresh meta into existing blocks without touching their `data` payload.
    // Payload: array of { uid, sid, type, meta }. Missing entries clear meta on that block.
    blocksMergeMeta: (state, action) => {
      const incoming = action.payload || [];
      const byUid = new Map(incoming.map((m) => [m.uid, m.meta || null]));
      state.list.forEach((block) => {
        if (!block?.uid) return;
        if (byUid.has(block.uid)) {
          block.meta = byUid.get(block.uid);
        } else if (block.meta) {
          block.meta = null;
        }
      });
    },
  },
});

export const {
  blocksUpdateActiveType,
  blocksUpdateActiveUID,
  blocksUpdateDragList,
  blocksUpdateTitle,
  blocksUpdateList,
  blocksUpdateInitialList,
  blocksUpdateArchivedList,
  blocksUpdateIsLoadingArchived,
  blocksReplaceBlock,
  blocksRefreshMetaRequest,
  blocksMergeMeta,
} = blocksSlice.actions;

export const selectBlocksActiveType = (state) => state.blocks.activeType;
export const selectBlocksActiveUID = (state) => state.blocks.activeUID;
export const selectBlocksDragList = (state) => state.blocks.dragList;
export const selectBlocksList = (state) => state.blocks.list;
export const selectBlocksInitialList = (state) => state.blocks.initialList;
export const selectBlocksArchivedList = (state) => state.blocks.archivedList;
export const selectBlocksIsLoadingArchived = (state) => state.blocks.isLoadingArchived;

export const selectBlocksHeading = createSelector([selectBlocksList, selectBlocksActiveUID], (blocksList, activeUID) => {
  return blocksList.find((el) => el.uid === activeUID)?.data?.blockHeading;
});

export const selectBlocksContent = createSelector([selectBlocksList, selectBlocksActiveUID], (blocksList, activeUID) => {
  return blocksList.find((el) => el.uid === activeUID)?.data?.blockContent;
});

export const selectBlocksDesign = createSelector([selectBlocksList, selectBlocksActiveUID], (blocksList, activeUID) => {
  return blocksList.find((el) => el.uid === activeUID)?.data?.blockDesign;
});

export const selectBlocksContainerDesign = createSelector([selectBlocksList, selectBlocksActiveUID], (blocksList, activeUID) => {
  return blocksList.find((el) => el.uid === activeUID)?.data?.blockDesign?.containerDesign;
});

export const selectBlocksItemDesign = createSelector([selectBlocksInitialList, selectBlocksActiveUID], (blocksList, activeUID) => {
  return blocksList.find((el) => el.uid === activeUID)?.data?.blockDesign?.itemDesign;
});

export default blocksSlice.reducer;
